import { styled } from '@mui/material/styles';
import { Paper, TableContainer, Button, DialogTitle, Box, DialogContent } from '@mui/material';

// Composant Paper stylisé avec animations
export const AnimatedPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: 'fadeInUp 0.6s ease-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2), 0 6px 12px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

// Composant TableContainer stylisé
export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  overflowX: 'auto',
  overflowY: 'hidden',
  maxWidth: '100%',
  border: `2px solid ${theme.palette.divider}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '& .MuiTableCell-root': {
    border: `1px solid ${theme.palette.divider}`,
    borderWidth: '0 1px 1px 0',
    '&:first-of-type': {
      borderLeftWidth: 0,
    },
    '&:last-of-type': {
      borderRightWidth: 0,
    },
  },
  '& .MuiTableHead .MuiTableCell-root': {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600,
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    border: `2px solid ${theme.palette.primary.light}`,
  },
}));

// Composant Button stylisé
export const StyledButton = styled(Button)(({ theme }) => ({
  border: `2px solid ${theme.palette.primary.main}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
    border: `2px solid ${theme.palette.primary.dark}`,
  },
}));

// Styles pour les lignes de tableau
export const tableRowHoverStyles = {
  transition: 'all 0.2s ease',
  '&:hover': { 
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    transform: 'scale(1.01)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    borderLeft: '3px solid',
    borderLeftColor: 'primary.main',
  }
};

// Styles pour les IconButton dans les tableaux (taille réduite)
export const iconButtonStyles = {
  border: '1px solid',
  borderColor: 'primary.main',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: '4px',
  minWidth: '28px',
  width: '28px',
  height: '28px',
  '& .MuiSvgIcon-root': {
    fontSize: '0.875rem',
  },
  '&:hover': {
    transform: 'scale(1.1) rotate(5deg)',
    backgroundColor: 'rgba(25, 118, 210, 0.1)',
    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
    borderColor: 'primary.dark',
  },
};

// Styles pour les IconButton d'export (taille normale)
export const exportIconButtonStyles = {
  border: '1px solid',
  borderColor: 'primary.main',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'scale(1.1) rotate(5deg)',
    backgroundColor: 'rgba(25, 118, 210, 0.1)',
    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
    borderColor: 'primary.dark',
  },
};

// Composant DialogTitle stylisé avec le même backgroundColor que le navbar
export const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  padding: theme.spacing(2),
}));

// Composant Box stylisé pour les formulaires avec double ombre
export const StyledFormBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15), 0 6px 12px rgba(0, 0, 0, 0.1)',
  },
}));

// Composant DialogContent stylisé avec double ombre pour les formulaires dans les dialogues
export const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08)',
  borderRadius: theme.shape.borderRadius,
  '&:first-of-type': {
    paddingTop: theme.spacing(3),
  },
}));
